/**
 * WarningsDataTable
 *
 * Renders the warnings (suivis) DataGrid (MUI) with filters, cell renderers,
 * and action buttons. This is "case 1" from the original CollabsView renderBody switch.
 */
import { useState, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { frFR, enUS } from "@mui/x-data-grid/locales";
import { useSelector } from "react-redux";
import Langs, { Localize } from "../../app/lang/langs";
import { checkPermission } from "../../app/utils/helpers";
import { config } from "../../app/utils/config";
import ImageButton from "../../components/buttons/ImageButton";
import userInfo from "../../assets/images/userInfo.png";
import userInfoFocus from "../../assets/images/userInfoFocus.png";
import deleteIcon from "../../assets/images/delete.png";
import deleteIconFocus from "../../assets/images/delete-orange.png";
import styles from "./CollabsView.module.css";
import {
  renderWarningDateCell,
  renderCategoryCell,
  renderWarningStatusCell,
  renderCreatedAtCell,
  renderUpdatedAtCell,
  renderCommentsCell,
  usePinnedActionsColumn,
  singleSelectFilterOperator,
} from "./collabsTableUtils";

/**
 * @param {Object} props
 * @param {Object} props.apiRef - MUI useGridApiRef() ref
 * @param {Array} props.data - The warnings list to display
 * @param {string} props.scrollHeight - CSS scroll height for the table
 * @param {Function} props.onViewDetails - Called when the "view details" button is clicked
 * @param {Function} props.onDelete - Called when the delete button is clicked with the warning id
 */
export default function WarningsDataTable({
  apiRef,
  data,
  scrollHeight,
  onViewDetails,
  onDelete,
}) {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [globalSearch, setGlobalSearch] = useState("");
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });
  const lang = useSelector((state) => state.settings.lang);
  const dataGridLocale = lang === "fr" ? frFR : enUS;

  const hasEditPermission = checkPermission(
    config.scopes.warnings,
    config.permissionTypes.edit
  );

  usePinnedActionsColumn(apiRef);

  const columns = useMemo(() => [
    { field: "id", headerName: Localize("ID"), minWidth: 112, sortable: true, filterable: true },
    { field: "collab_matricule", headerName: Localize("Matricule"), minWidth: 128, sortable: true, filterable: true },
    { field: "collab_nom", headerName: Localize("Family Name"), minWidth: 128, sortable: true, filterable: true },
    { field: "collab_prenom", headerName: Localize("Name"), minWidth: 144, sortable: true, filterable: true },
    {
      field: "category", headerName: Localize("category"), minWidth: 176, sortable: true, filterable: true,
      filterOperators: [singleSelectFilterOperator([
        { value: "warning", label: Localize("warning") },
        { value: "recall the order", label: Localize("recall the order") },
        { value: "formal notice", label: Localize("formal notice") },
        { value: "Demande rupture conventionnelle", label: Localize("Demande rupture conventionnelle") },
        { value: "Demande prime", label: Localize("Demande prime") },
        { value: "Demande augmentation salaire", label: Localize("Demande augmentation salaire") },
        { value: "Mise à pied", label: Localize("Mise à pied") },
        { value: "Mutation disciplinaire", label: Localize("Mutation disciplinaire") },
        { value: "Rétrogradation disciplinaire", label: Localize("Rétrogradation disciplinaire") },
      ])],
      renderCell: renderCategoryCell,
    },
    {
      field: "status", headerName: Localize("statut"), minWidth: 144, sortable: true, filterable: true,
      filterOperators: [singleSelectFilterOperator([
        { value: "received", label: Localize("received") },
        { value: "signed", label: Localize("signed") },
        { value: "sent", label: Localize("sent") },
      ])],
      renderCell: renderWarningStatusCell,
    },
    { field: "team_name", headerName: Localize("Client"), minWidth: 144, sortable: true, filterable: true },
    {
      field: "warning_date", headerName: Localize("Date"), minWidth: 144,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderWarningDateCell,
    },
    {
      field: "created_at", headerName: Localize("created_at"), minWidth: 144,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderCreatedAtCell,
    },
    {
      field: "updated_at", headerName: Localize("updated_at"), minWidth: 144,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderUpdatedAtCell,
    },
    {
      field: "comments", headerName: Localize("DESCRIPTION"), minWidth: 144,
      sortable: false, filterable: false,
      renderCell: renderCommentsCell,
    },
    {
      field: "actions", headerName: "", width: 96, sortable: false, filterable: false,
      disableColumnMenu: true, disableReorder: true,
      renderCell: (params) => (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          {hasEditPermission && (
            <ImageButton
              image={userInfo}
              imageFocus={userInfoFocus}
              onClick={() => onViewDetails(params.row)}
              active={true}
              tooltip={<Langs str="VIEW_DETAILS" />}
              width={32}
            />
          )}
          {hasEditPermission && (
            <ImageButton
              image={deleteIcon}
              imageFocus={deleteIconFocus}
              onClick={() => onDelete(params.row.id)}
              active={true}
              tooltip={<Langs str="DELETE_WARNING" />}
              width={32}
            />
          )}
        </div>
      ),
    },
  ], [hasEditPermission, onViewDetails, onDelete]);

  return (
    <div className={styles.collabsCard}>
      <div className="p-2 mb-1">
        <input
          type="text"
          className="form-control"
          placeholder={Localize("Search") + "..."}
          value={globalSearch}
          onChange={(e) => {
            const val = e.target.value;
            setGlobalSearch(val);
            setFilterModel((prev) => ({
              ...prev,
              quickFilterValues: val ? val.split(" ") : [],
            }));
          }}
          style={{ maxWidth: 350 }}
        />
      </div>
      <div style={{ height: scrollHeight, width: "100%" }}>
        <DataGrid
          localeText={dataGridLocale.components.MuiDataGrid.defaultProps.localeText}
          apiRef={apiRef}
          rows={data}
          columns={columns}
          getRowId={(row) => row.id}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          filterModel={filterModel}
          onFilterModelChange={(newModel) => {
            setFilterModel((prev) => ({
              ...newModel,
              quickFilterValues: prev.quickFilterValues,
            }));
          }}
          disableRowSelectionOnClick
          getRowHeight={() => "auto"}
          sx={{
            border: "none",
            "& .MuiDataGrid-cell": { display: "flex", alignItems: "center", py: 1 },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #e0e0e0",
              minHeight: 52,
            },
            "& .MuiTablePagination-root": {
              width: "100%",
              overflow: "hidden",
            },
            "& .MuiTablePagination-toolbar": {
              justifyContent: "flex-end",
              alignItems: "center",
              minHeight: 52,
              px: 2,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              margin: 0,
            },
            '& .MuiDataGrid-cell[data-field="actions"]': {
              backgroundColor: "var(--surface-card, #fff)",
            },
          }}
        />
      </div>
    </div>
  );
}
